// src/modulos/Parametros/Tipos/TypeProductPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useSubCollection from '../../../hooks/useSubCollection';
import usePagePermissions from '../../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../../components/common';

// Import utilities
import { getTimestamps } from '../../../utils/timestamps';

// Page-specific styles
import styles from '../Parameters.module.css';

// Constantes
const PRODUCT_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-produto'
};
const ITEMS_PER_PAGE = 10;

const TypeProductPage = () => {
    const can = usePagePermissions('tipo_de_produto');

    // Estados Locais
    const [editingType, setEditingType] = useState(null);
    const [nameInput, setNameInput] = useState('');
    const [noCalculateDiscountCommission, setNoCalculateDiscountCommission] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: productTypes,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useSubCollection(
        PRODUCT_TYPE_COLLECTION_PATH.parentCollection,
        PRODUCT_TYPE_COLLECTION_PATH.parentId,
        PRODUCT_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );

    // ============================================================
    // 1. DEFINIR filteredTypes ANTES dos callbacks
    // ============================================================
    const filteredTypes = useMemo(() => {
        if (!productTypes) return [];
        if (!searchTerm) return productTypes;

        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return productTypes.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [productTypes, searchTerm]);

    const totalFilteredItems = filteredTypes.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const paginatedTypes = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredTypes.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredTypes, currentPage]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingType(null);
        setNameInput('');
        setNoCalculateDiscountCommission(false);
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingType(item);
        setNameInput(item.name);
        setNoCalculateDiscountCommission(!!item.noCalculateDiscountCommission);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Tipo de Produto "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const newTotalItems = totalFilteredItems - 1;
                const maxPage = Math.ceil(newTotalItems / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                } else if (newTotalItems === 0) {
                    setCurrentPage(1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Tipo de Produto: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar tipo de produto. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, totalFilteredItems, currentPage]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("O nome do Tipo de Produto é obrigatório.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingType, userEmail);
        const data = {
            name: nameInput.trim(),
            noCalculateDiscountCommission: noCalculateDiscountCommission,
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingType) {
                result = await updateDocument(editingType.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingType ? 'Tipo de Produto editado com sucesso!' : 'Tipo de Produto adicionado com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingType ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, noCalculateDiscountCommission, editingType, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // Função para exibir status
    const renderDiscountCommissionStatus = useCallback((value) => {
        return value ? '✅ Sim' : '❌ Não';
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingType ? `Editar: ${editingType.name}` : 'Adicionar Novo Tipo de Produto'}
                subtitle={editingType ? `Editando: ${editingType.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingType ? '💾 Salvar Edição' : '✨ Adicionar Tipo'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome do Tipo de Produto <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className={styles.input}
                        placeholder="Ex: Matéria Prima, Produto Acabado, Serviço"
                        required
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Nome que identifica a categoria do produto
                    </small>
                </div>

                <div className={styles.inputGroup}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={noCalculateDiscountCommission}
                            onChange={(e) => setNoCalculateDiscountCommission(e.target.checked)}
                            disabled={formLoading}
                        />
                        <span>Não calcular Desconto/Comissão neste Tipo de Produto</span>
                    </label>
                    <small className={styles.fieldHint}>
                        Marque esta opção se produtos deste tipo não devem participar de cálculos de desconto ou comissão
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar o Tipo de Produto "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Tipos de Produto"
                actionText="Adicionar Tipo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar tipo de produto..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={3} rows={ITEMS_PER_PAGE} />
                    ) : paginatedTypes.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '🏷️'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum tipo de produto cadastrado'
                            }
                            message={
                                searchTerm
                                    ? `Não encontramos resultados para "${searchTerm}"`
                                    : "Clique em '+ Adicionar Tipo' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nome do Tipo de Produto</th>
                                            <th>Não Calcula Desc./Comissão</th>
                                            <th style={{ width: '150px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedTypes.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Atualizado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td>{renderDiscountCommissionStatus(item.noCalculateDiscountCommission)}</td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="tipos"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default TypeProductPage;